import type { AppsPreset } from "../apps/types";
import type { AccessCatalog, AccessNode } from "./types";

export type AccessPresetSource = Pick<AppsPreset, "categories">;

const NODE_ID_SEPARATOR = "/";

export function accessNodeId(
  categoryId: string,
  appId?: string,
  sectionId?: string,
): string {
  return [categoryId, appId, sectionId]
    .filter((part): part is string => Boolean(part))
    .join(NODE_ID_SEPARATOR);
}

export function buildAccessCatalog(preset: AccessPresetSource): AccessCatalog {
  const nodes: Record<string, AccessNode> = {};
  const children: Record<string, string[]> = {};
  const roots: string[] = [];
  const order: string[] = [];
  const appCategories: Record<string, string[]> = {};

  const register = (node: AccessNode): void => {
    nodes[node.id] = node;
    children[node.id] = children[node.id] ?? [];
    order.push(node.id);

    if (node.parentId === null) {
      roots.push(node.id);
      return;
    }

    children[node.parentId] = children[node.parentId] ?? [];
    children[node.parentId].push(node.id);
  };

  Object.entries(preset.categories ?? {}).forEach(([categoryId, category]) => {
    const categoryNodeId = accessNodeId(categoryId);

    register({
      id: categoryNodeId,
      level: "category",
      parentId: null,
      categoryId,
      appId: null,
      sectionId: null,
    });

    Object.entries(category.apps ?? {}).forEach(([appId, app]) => {
      const appNodeId = accessNodeId(categoryId, appId);

      register({
        id: appNodeId,
        level: "app",
        parentId: categoryNodeId,
        categoryId,
        appId,
        sectionId: null,
      });

      appCategories[appId] = [...(appCategories[appId] ?? []), categoryId];

      Object.keys(app.menu ?? {}).forEach((sectionId) => {
        register({
          id: accessNodeId(categoryId, appId, sectionId),
          level: "section",
          parentId: appNodeId,
          categoryId,
          appId,
          sectionId,
        });
      });
    });
  });

  return { nodes, children, roots, order, appCategories };
}

export function tenantAllowedNodeIds(preset: AccessPresetSource): Set<string> {
  const allowed = new Set<string>();

  Object.entries(preset.categories ?? {}).forEach(([categoryId, category]) => {
    if (!category.enabled) return;
    allowed.add(accessNodeId(categoryId));

    Object.entries(category.apps ?? {}).forEach(([appId, app]) => {
      if (app.enabled === false) return;
      allowed.add(accessNodeId(categoryId, appId));

      Object.entries(app.menu ?? {}).forEach(([sectionId, visible]) => {
        if (!visible) return;
        allowed.add(accessNodeId(categoryId, appId, sectionId));
      });
    });
  });

  return allowed;
}

export function getAccessNode(
  catalog: AccessCatalog,
  nodeId: string,
): AccessNode | undefined {
  return catalog.nodes[nodeId];
}

export function getAccessChildren(
  catalog: AccessCatalog,
  nodeId: string,
): string[] {
  return catalog.children[nodeId] ?? [];
}

export function getAccessAncestors(
  catalog: AccessCatalog,
  nodeId: string,
): string[] {
  const chain: string[] = [];
  let current = catalog.nodes[nodeId]?.parentId ?? null;

  while (current !== null) {
    chain.unshift(current);
    current = catalog.nodes[current]?.parentId ?? null;
  }

  return chain;
}

export function getAccessDescendants(
  catalog: AccessCatalog,
  nodeId: string,
): string[] {
  const descendants: string[] = [];
  const pending = [...getAccessChildren(catalog, nodeId)];

  while (pending.length > 0) {
    const next = pending.pop();
    if (next === undefined) break;

    descendants.push(next);
    pending.push(...getAccessChildren(catalog, next));
  }

  return descendants;
}
